import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';
import { Nurse, ROLE_TYPES } from '../../types/nurse';
import { GenderAgeDisplay, NameNicknameDisplay } from '../common';
import { notifyAction } from '../common/ActionNotifier';
import nursesApi from '../../api/nurses';

interface NurseInfo {
  title: string;
  nurse: Nurse | null;
  roleLabel: (roleType: string) => string;
  position: 'left' | 'right';
  isSelected?: boolean;
}

interface AssignNurseDialogCommonProps {
  open: boolean;
  onClose: () => void;
  nurseId: number;
  onSuccess: () => void;
  title: string;
  mode: 'assigned' | 'support';
  fixedNurse: Nurse | null;
  fixedNurseTitle: string;
  fixedNurseRoleLabel: (roleType: string) => string;
  selectedNurseTitle: string;
  selectedNurseRoleLabel: (roleType: string) => string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  buttonText?: string;
  fixedNurseOnLeft?: boolean;
}

const AssignNurseDialogCommon: React.FC<AssignNurseDialogCommonProps> = ({
  open,
  onClose,
  nurseId,
  onSuccess,
  title,
  mode,
  fixedNurse,
  fixedNurseTitle,
  fixedNurseRoleLabel,
  selectedNurseTitle,
  selectedNurseRoleLabel,
  searchPlaceholder = "名前またはニックネームで検索",
  emptyMessage = "条件に一致する看護師が見つかりませんでした",
  buttonText = "登録",
  fixedNurseOnLeft = true
}) => {
  const theme = useTheme();
  const [ availableNurses, setAvailableNurses ] = useState<Nurse[]>([]);
  const [ selectedNurseId, setSelectedNurseId ] = useState<number | null>(null);
  const [ selectedNurse, setSelectedNurse ] = useState<Nurse | null>(null);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);
  const [ searchTerm, setSearchTerm ] = useState('');
  // 看護師一覧を取得
  useEffect(() => {
    if (open) {
      fetchAvailableNurses();
    }
  }, [ open, nurseId, mode ]);
  const fetchAvailableNurses = async() => {
    setIsLoading(true);
    setError(null);
    try {
      let nurses: Nurse[] = [];
      if (mode === 'assigned') {
        nurses = await nursesApi.getAvailableAssignedNurses(nurseId);
      } else {
        nurses = await nursesApi.getAvailableSupportNurses(nurseId);
      }
      setAvailableNurses(nurses);
      if (nurses.length === 0) {
        setError(`登録可能な看護師が見つかりませんでした`);
      }
    } catch (error) {
      console.error('看護師一覧取得エラー:', error);
      setError('看護師一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };
  // 看護師登録処理
  const handleSubmit = async() => {
    if (!selectedNurseId) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      let response;
      if (mode === 'assigned') {
        response = await nursesApi.assignNurse(nurseId, selectedNurseId);
      } else {
        response = await nursesApi.assignSupportNurse(nurseId, selectedNurseId);
      }
      if (response.success) {
        // 選択された看護師と固定された看護師の情報を取得
        const selectedNurseInfo = availableNurses.find(n => n.id === selectedNurseId);
        const supportNurse = mode === 'assigned' ? fixedNurse : selectedNurseInfo;
        const regularNurse = mode === 'assigned' ? selectedNurseInfo : fixedNurse;
        // サポート看護師の名前とニックネーム
        const supportName = supportNurse?.name || '';
        const supportNickname = supportNurse?.nickname || '';
        // 担当看護師の名前とニックネーム
        const regularName = regularNurse?.name || '';
        const regularNickname = regularNurse?.nickname || '';
        // 指定された形式でメッセージを構築
        const successMessage = `作成しました: [ ${supportName} / ${supportNickname} ] - [ ${regularName} / ${regularNickname} ]`;
        notifyAction({
          message: successMessage,
          severity: 'success',
          duration: 10000  // 表示時間延: 10秒
        });
        // 通知の表示時間を確保するため、少しディレイを入れてからクローズ
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 300);
      } else {
        const errorMsg = '登録に失敗しました: ' + response.message;
        setError(errorMsg);
        // エラー通知を表示 
        notifyAction({
          message: errorMsg,
          severity: 'error',
          duration: 10000  // 表示時間: 10秒
        });
      }
    } catch (error: any) {
      console.error('看護師登録エラー:', error);
      const errorMsg = error.response?.data?.error ||
        error.response?.data?.details?.error ||
        '登録処理中にエラーが発生しました';
      setError(errorMsg);
      // エラー通知を表示
      notifyAction({
        message: errorMsg,
        severity: 'error',
        duration: 10000  // 表示時間: 10秒
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  // 検索条件に一致する看護師をフィルタリング
  const filteredNurses = availableNurses.filter(nurse => {
    const searchLower = searchTerm.toLowerCase();
    return (
      nurse.name.toLowerCase().includes(searchLower) ||
      nurse.nickname.toLowerCase().includes(searchLower)
    );
  });
  // 選択した看護師の情報を更新
  const handleNurseSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nurseId = Number(event.target.value);
    setSelectedNurseId(nurseId);
    const selected = availableNurses.find(nurse => nurse.id === nurseId) || null;
    setSelectedNurse(selected);
  };
  // 左右に表示する看護師の情報を設定
  const leftNurse: NurseInfo = fixedNurseOnLeft
    ? {
      title: fixedNurseTitle,
      nurse: fixedNurse,
      roleLabel: fixedNurseRoleLabel,
      position: 'left'
    }
    : {
      title: selectedNurseTitle,
      nurse: selectedNurse,
      roleLabel: selectedNurseRoleLabel,
      position: 'left',
      isSelected: true
    };
  const rightNurse: NurseInfo = fixedNurseOnLeft
    ? {
      title: selectedNurseTitle,
      nurse: selectedNurse,
      roleLabel: selectedNurseRoleLabel,
      position: 'right',
      isSelected: true
    }
    : {
      title: fixedNurseTitle,
      nurse: fixedNurse,
      roleLabel: fixedNurseRoleLabel,
      position: 'right'
    };
  // 看護師情報表示コンポーネント
  const renderNurseInfo = (info: NurseInfo) => {
    const isSelected = info.isSelected && info.nurse;
    return (
      <Grid item xs={6}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 0.5 }}>
          {info.title}
        </Typography>
        {info.nurse ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 1,
            bgcolor: theme.palette.common.white,
            borderRadius: 1,
            border: isSelected ? `1px solid ${theme.palette.primary.light}` : `1px solid ${theme.palette.divider}`,
            boxShadow: isSelected ? `0 0 0 1px ${theme.palette.primary.light}30` : 'none'
          }}>
            <NameNicknameDisplay
              name={info.nurse.name}
              nickname={info.nurse.nickname}
              nameFontWeight="bold"
              isTestAccount={info.nurse.is_test_account}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <GenderAgeDisplay
                genderType={info.nurse.gender_type}
                birthday={info.nurse.birthday}
                fontSize="0.85rem"
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.85rem',
                  color: 'text.secondary',
                  ml: 2
                }}
              >
                {ROLE_TYPES[info.nurse.role_type as keyof typeof ROLE_TYPES] || info.nurse.role_type}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.disabled }}>
            {info.isSelected ? '選択してください' : '情報なし'}
          </Typography>
        )}
      </Grid>
    );
  };
  return (
    <Dialog
      open={open}
      onClose={!isSubmitting ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {title}
      </DialogTitle>
      <Divider/>

      {/* 看護師情報表示エリア */}
      <Box sx={{ px: 3, py: 2, backgroundColor: theme.palette.grey[50], borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Grid container spacing={2}>
          {renderNurseInfo(leftNurse)}
          {renderNurseInfo(rightNurse)}
        </Grid>
      </Box>

      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress/>
          </Box>
        ) : error && availableNurses.length === 0 ? (
          <Typography color="error" align="center" sx={{ my: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />

            <Paper
              variant="outlined"
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                p: 0,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[300],
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: theme.palette.grey[400],
                  }
                }
              }}
            >
              {filteredNurses.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </Box>
              ) : (
                <RadioGroup
                  value={selectedNurseId}
                  onChange={handleNurseSelection}
                >
                  {filteredNurses.map((nurse) => (
                    <FormControlLabel
                      key={nurse.id}
                      value={nurse.id}
                      control={<Radio/>}
                      label={
                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
                          <NameNicknameDisplay
                            name={nurse.name}
                            nickname={nurse.nickname}
                            nameFontWeight="bold"
                            isTestAccount={nurse.is_test_account}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <GenderAgeDisplay
                              genderType={nurse.gender_type}
                              birthday={nurse.birthday}
                              fontSize="0.85rem"
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.85rem',
                                color: 'text.secondary',
                                ml: 2
                              }}
                            >
                              {ROLE_TYPES[nurse.role_type as keyof typeof ROLE_TYPES] || nurse.role_type}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        display: 'flex',
                        py: 1,
                        px: 2,
                        m: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        }
                      }}
                    />
                  ))}
                </RadioGroup>
              )}
            </Paper>

            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          variant="outlined"
          color="inherit"
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedNurseId || isLoading}
          variant="contained"
          color="primary"
          startIcon={isSubmitting ? <CircularProgress size={20}/> : <AddIcon/>}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AssignNurseDialogCommon;
